import {
    ComplianceInput,
    ComplianceStatus,
    CategoryProgress,
    PlanCategory,
    TrainingCompletion,
    TrainingTopic,
    TrainingQuizResponse,
    PlanValidation,
    TopicStyle,
    TrainingTopicInput,
    ImportResult,
    DEFAULT_WORKING_DAYS,
} from '../types/trainingTypes';

/**
 * Parses a YYYY-MM-DD date string as a local date (not UTC).
 * `new Date('2026-04-16')` treats it as UTC midnight, which shifts the day
 * in timezones behind UTC. This splits the string and constructs a local Date.
 */
export function parseLocalDateStr(dateStr: string): Date {
    const [y, m, d] = dateStr.split('-').map(Number);
    return new Date(y, m - 1, d);
}

/**
 * Returns today's date as a YYYY-MM-DD string using local timezone components.
 */
export function getLocalTodayStr(): string {
    const n = new Date();
    return `${n.getFullYear()}-${String(n.getMonth() + 1).padStart(2, '0')}-${String(n.getDate()).padStart(2, '0')}`;
}

/**
 * Classifies a user's compliance status based on their progress ratio.
 * - on-track: progress ratio >= 0.9
 * - behind: progress ratio >= 0.6 and < 0.9
 * - at-risk: progress ratio < 0.6
 *
 * Returns 'on-track' when totalCycleDays or totalMinutesRequired is 0.
 */
export function calculateComplianceStatus(input: ComplianceInput): ComplianceStatus {
    const { totalMinutesViewed, totalMinutesRequired, daysElapsed, totalCycleDays } = input;
    if (totalCycleDays === 0 || totalMinutesRequired === 0) return 'on-track';

    const expectedProgress = (daysElapsed / totalCycleDays) * totalMinutesRequired;
    const progressRatio = totalMinutesViewed / expectedProgress;

    if (progressRatio >= 0.9) return 'on-track';
    if (progressRatio >= 0.6) return 'behind';
    return 'at-risk';
}

/**
 * Computes per-category completed minutes and percentage from completions.
 * Percentage is capped at 100%.
 */
export function calculateCategoryProgress(
    categories: PlanCategory[],
    completions: TrainingCompletion[],
    _topics: TrainingTopic[]
): CategoryProgress[] {
    return categories.map(cat => {
        const catCompletions = completions.filter(c => c.categoryId === cat.id);
        const completedMinutes = catCompletions.reduce((sum, c) => sum + c.timeValueMinutes, 0);
        return {
            categoryId: cat.id,
            categoryName: cat.name,
            targetMinutes: cat.targetMinutes,
            completedMinutes,
            percentage: cat.targetMinutes > 0 ? Math.min(100, (completedMinutes / cat.targetMinutes) * 100) : 0,
        };
    });
}

/**
 * Validates plan categories: checks for at least one category, non-empty names,
 * positive targets, and total assigned vs minimum time required.
 */
export function validatePlanCategories(
    categories: PlanCategory[],
    minimumTimeRequired: number
): PlanValidation {
    const errors: string[] = [];
    if (categories.length === 0) errors.push('At least one category is required.');
    const totalAssigned = categories.reduce((sum, c) => sum + c.targetMinutes, 0);
    if (totalAssigned < minimumTimeRequired) {
        errors.push(`Total assigned (${totalAssigned} min) is ${minimumTimeRequired - totalAssigned} min short of requirement.`);
    }
    categories.forEach(c => {
        if (!c.name.trim()) errors.push('All categories must have a name.');
        if (c.targetMinutes <= 0) errors.push(`Category "${c.name}" must have a positive time target.`);
    });
    return { valid: errors.length === 0, errors };
}

/**
 * Filters topics by style and search query (case-insensitive match on Topic ID and Description).
 */
export function filterTopics(
    topics: TrainingTopic[],
    query: string,
    styleFilter: TopicStyle | 'All'
): TrainingTopic[] {
    const lowerQuery = query.toLowerCase().trim();
    return topics.filter(t => {
        const matchesStyle = styleFilter === 'All' || t.style === styleFilter;
        const matchesQuery = !lowerQuery ||
            t.topicId.toLowerCase().includes(lowerQuery) ||
            t.description.toLowerCase().includes(lowerQuery);
        return matchesStyle && matchesQuery;
    });
}

/**
 * Checks if a date's day-of-week is in the working days array.
 * Defaults to Mon–Fri ([1,2,3,4,5]) if workingDays is not provided.
 */
export function isWorkingDay(date: Date, workingDays: number[] = DEFAULT_WORKING_DAYS): boolean {
    const day = date.getDay();
    return workingDays.includes(day);
}

/**
 * Calculates the next due date by adding cycleDurationDays to the affidavit date.
 */
export function calculateNextDueDate(affidavitDate: Date, cycleDurationDays: number): Date {
    const nextDue = new Date(affidavitDate);
    nextDue.setDate(nextDue.getDate() + cycleDurationDays);
    return nextDue;
}

/**
 * Partitions import rows into imported and skipped based on duplicate IDs.
 * Rows with topicIds already in existingTopicIds are skipped.
 */
export function processTopicImport(
    rows: TrainingTopicInput[],
    existingTopicIds: string[]
): ImportResult {
    const imported: TrainingTopicInput[] = [];
    const skipped: { topicId: string; reason: string }[] = [];
    for (const row of rows) {
        if (existingTopicIds.includes(row.topicId)) {
            skipped.push({ topicId: row.topicId, reason: 'Duplicate ID already exists' });
        } else {
            imported.push(row);
        }
    }
    return { imported, skipped };
}

/**
 * Returns a status badge string based on launched and markedAsViewed flags.
 * - "Viewed" when launched=true AND markedAsViewed=true
 * - "Marked-Not Launched" when launched=false AND markedAsViewed=true
 * - "Not Viewed" when markedAsViewed=false
 */
export function getCompletionStatusBadge(launched: boolean, markedAsViewed: boolean): string {
    if (markedAsViewed && launched) return 'Viewed';
    if (markedAsViewed && !launched) return 'Marked-Not Launched';
    return 'Not Viewed';
}

/**
 * Computes the current day number in the training cycle.
 * Day 1 is the enrollment start date. Wraps around using modulo on cycleDuration.
 */
export function calculateDayNumberInCycle(
    enrollmentStartDate: Date,
    currentDate: Date,
    cycleDuration: number
): number {
    const startTime = new Date(enrollmentStartDate.getFullYear(), enrollmentStartDate.getMonth(), enrollmentStartDate.getDate()).getTime();
    const currentTime = new Date(currentDate.getFullYear(), currentDate.getMonth(), currentDate.getDate()).getTime();
    const daysElapsed = Math.floor((currentTime - startTime) / (1000 * 60 * 60 * 24));
    return (daysElapsed % cycleDuration) + 1;
}

/**
 * Returns true if the current date is after the scheduled date (topic is locked).
 */
export function isTopicLocked(scheduledDate: Date, currentDate: Date): boolean {
    const scheduled = new Date(scheduledDate.getFullYear(), scheduledDate.getMonth(), scheduledDate.getDate());
    const current = new Date(currentDate.getFullYear(), currentDate.getMonth(), currentDate.getDate());
    return current.getTime() > scheduled.getTime();
}

/**
 * Calculates the notification date by subtracting daysBefore from the due date.
 */
export function calculateNotificationDate(dueDate: Date, daysBefore: number): Date {
    const notificationDate = new Date(dueDate);
    notificationDate.setDate(notificationDate.getDate() - daysBefore);
    return notificationDate;
}

/**
 * Computes plan duration statistics based on cycle duration, minimum time, and working days config.
 * Returns working days count, equivalent hours, avg minutes per working day, and estimated topics.
 */
export function calculatePlanDurationStats(
    cycleDuration: number,
    minimumTimeRequired: number,
    workingDays: number[] = DEFAULT_WORKING_DAYS
): {
    workingDaysCount: number;
    equivalentHours: number;
    avgMinutesPerWorkingDay: number;
    estimatedTopics: number;
} {
    // Count how many working days exist in the cycle
    // We iterate through each day of the cycle and check if its day-of-week is a working day
    let workingDaysCount = 0;
    const today = new Date();
    for (let i = 0; i < cycleDuration; i++) {
        const date = new Date(today);
        date.setDate(today.getDate() + i);
        if (workingDays.includes(date.getDay())) {
            workingDaysCount++;
        }
    }

    const equivalentHours = minimumTimeRequired / 60;
    const avgMinutesPerWorkingDay = workingDaysCount > 0 ? minimumTimeRequired / workingDaysCount : 0;
    // Estimated topics at 5 min/topic average
    const estimatedTopics = minimumTimeRequired > 0 ? Math.ceil(minimumTimeRequired / 5) : 0;

    return {
        workingDaysCount,
        equivalentHours,
        avgMinutesPerWorkingDay,
        estimatedTopics,
    };
}


// ---------------------------------------------------------------------------
// Holiday computation — US Federal holidays
// ---------------------------------------------------------------------------

/** Helper: get the Nth occurrence of a weekday in a given month/year. */
function nthWeekdayOfMonth(year: number, month: number, weekday: number, n: number): Date {
    const first = new Date(year, month, 1);
    const dayOfWeek = first.getDay();
    let diff = weekday - dayOfWeek;
    if (diff < 0) diff += 7;
    const firstOccurrence = 1 + diff;
    const date = firstOccurrence + (n - 1) * 7;
    return new Date(year, month, date);
}

/** Helper: get the last occurrence of a weekday in a given month/year. */
function lastWeekdayOfMonth(year: number, month: number, weekday: number): Date {
    const lastDay = new Date(year, month + 1, 0);
    let diff = lastDay.getDay() - weekday;
    if (diff < 0) diff += 7;
    return new Date(year, month, lastDay.getDate() - diff);
}

/** Format a Date to YYYY-MM-DD string. */
function toYMD(d: Date): string {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
}

export interface HolidayEntry {
    date: string; // YYYY-MM-DD
    name: string;
}

/**
 * Computes the 7 default US federal holidays for a given year:
 * - New Year's Day (Jan 1)
 * - Memorial Day (last Monday of May)
 * - Independence Day (Jul 4)
 * - Labor Day (1st Monday of Sep)
 * - Thanksgiving Thursday (4th Thursday of Nov)
 * - Thanksgiving Friday (day after Thanksgiving)
 * - Christmas Day (Dec 25)
 */
export function getDefaultHolidaysForYear(year: number): HolidayEntry[] {
    return [
        { date: `${year}-01-01`, name: "New Year's Day" },
        { date: toYMD(lastWeekdayOfMonth(year, 4, 1)), name: 'Memorial Day' },
        { date: `${year}-07-04`, name: 'Independence Day' },
        { date: toYMD(nthWeekdayOfMonth(year, 8, 1, 1)), name: 'Labor Day' },
        { date: toYMD(nthWeekdayOfMonth(year, 10, 4, 4)), name: 'Thanksgiving' },
        { date: toYMD(new Date(nthWeekdayOfMonth(year, 10, 4, 4).getTime() + 86400000)), name: 'Day After Thanksgiving' },
        { date: `${year}-12-25`, name: 'Christmas Day' },
    ];
}

/**
 * Computes all default holidays that fall within a date range.
 * Covers all years the range spans.
 */
export function getDefaultHolidaysInRange(startDate: string, endDate: string): HolidayEntry[] {
    const startYear = parseInt(startDate.substring(0, 4), 10);
    const endYear = parseInt(endDate.substring(0, 4), 10);
    const all: HolidayEntry[] = [];
    for (let y = startYear; y <= endYear; y++) {
        all.push(...getDefaultHolidaysForYear(y));
    }
    return all.filter(h => h.date >= startDate && h.date <= endDate);
}


// ---------------------------------------------------------------------------
// Quiz Eligibility
// ---------------------------------------------------------------------------

export type QuizEligibilityStatus = 'not-eligible' | 'quiz-due' | 'retry-tomorrow' | 'passed';

/**
 * Determines whether a user should be prompted to take a category quiz.
 *
 * @param category - The plan category (must have requiresQuiz + quizFormId)
 * @param userCategoryMinutes - Total minutes the user has accumulated in this category
 * @param quizResponses - All quiz responses for this user/plan/category in the current cycle
 * @returns QuizEligibilityStatus
 */
export function checkQuizEligibility(
    category: PlanCategory,
    userCategoryMinutes: number,
    quizResponses: TrainingQuizResponse[],
): QuizEligibilityStatus {
    // Category doesn't require a quiz
    if (!category.requiresQuiz || !category.quizFormId) {
        return 'not-eligible';
    }

    // User hasn't met the minimum time requirement yet
    if (userCategoryMinutes < category.targetMinutes) {
        return 'not-eligible';
    }

    // Check if user already passed the quiz this cycle
    const passingResponse = quizResponses.find(r => r.passed);
    if (passingResponse) {
        return 'passed';
    }

    // Check if user already failed today (must wait until tomorrow)
    const todayStr = getLocalTodayStr();
    const failedToday = quizResponses.some(r => {
        if (r.passed) return false;
        const completedDate = r.completedAt.split('T')[0];
        return completedDate === todayStr;
    });

    if (failedToday) {
        return 'retry-tomorrow';
    }

    // User is eligible and hasn't passed or failed today
    return 'quiz-due';
}

/**
 * Calculates accumulated minutes for a specific category from completions.
 */
export function getCategoryAccumulatedMinutes(
    categoryId: string,
    completions: TrainingCompletion[],
): number {
    return completions
        .filter(c => c.categoryId === categoryId)
        .reduce((sum, c) => sum + c.timeValueMinutes, 0);
}
